import React from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { JobsService } from '@/api/generated/services/JobsService'
import type { JobResponse } from '@/api/generated/models/JobResponse'

/**
 * AI 분석 단계별 소요시간 모달 — 임시 테스트용 계측 (2026-11).
 *
 * AI 분석 흐름: 프론트 작업 등록(POST) → 워커가 항목을 잡음 → 과제·첨부 텍스트 준비 →
 * SLM 동기 호출(응답이 곧 결과, 별도 콜백 없음) → 저장·작업 종결 → 프론트 폴링(3초)이 완료 감지.
 * 백엔드 단계는 item.result.timing(서버 시계)에서, 프론트 단계는 브라우저 시계로 잰다.
 */

export interface AiTimingStage {
  label: string
  ms: number
  note?: string
}

/** 클릭 시각(t0) · 등록 응답 시각(t1) · 완료 감지 시각(t2) + 종료된 job → 단계별 소요시간 */
export const buildAiTimingStages = async (
  job: JobResponse,
  t0: number,
  t1: number,
  t2: number
): Promise<AiTimingStage[]> => {
  const res = await JobsService.appsJobsViewsListJobItems({ jobId: job.id, perPage: 100 })
  const timings = res.data
    .map(item => item.result?.timing as Record<string, number> | undefined)
    .filter((t): t is Record<string, number> => !!t)
  const sum = (key: string) => timings.reduce((acc, t) => acc + (t[key] ?? 0), 0)
  const queueWait = timings.length ? Math.min(...timings.map(t => t.queue_wait_ms ?? 0)) : 0
  const prep = sum('prep_ms')
  const ai = sum('ai_ms')
  const calls = sum('ai_calls')

  const serverSpan = job.finished_at
    ? new Date(job.finished_at).getTime() - new Date(job.created_at).getTime()
    : 0
  const request = t1 - t0
  const backendAfterAi = Math.max(0, serverSpan - queueWait - prep - ai)
  const delivery = Math.max(0, t2 - t0 - request - serverSpan)

  return [
    { label: '프론트 → 백엔드 요청', ms: request, note: '분석 작업 등록(POST) 응답까지' },
    { label: '백엔드 → AI 요청', ms: queueWait + prep, note: `작업 대기 ${fmt(queueWait)} + 과제·첨부 준비 ${fmt(prep)}` },
    { label: 'AI 결과 생성', ms: ai, note: `SLM 호출 ${calls}회 · 동시 호출은 겹친 시간 한 번만` },
    { label: 'AI → 백엔드 반영', ms: backendAfterAi, note: '응답 저장·작업 종결 처리' },
    { label: '백엔드 → 프론트 전달', ms: delivery, note: '완료 감지(3초 간격 폴링)·결과 재조회' },
    { label: '전체', ms: t2 - t0 },
  ]
}

const fmt = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}초` : `${Math.round(ms)}ms`)

interface AiTimingModalProps {
  stages: AiTimingStage[] | null
  onClose: () => void
}

const AiTimingModal: React.FC<AiTimingModalProps> = ({ stages, onClose }) => {
  if (!stages) return null
  const sans = designTokens.typography.fontFamily.sans[0]

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      data-modal-overlay
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white" style={{ width: '440px', padding: '20px', borderRadius: '4px', fontFamily: sans }}>
        <div
          className="flex justify-between items-center"
          style={{ paddingBottom: '8px', borderBottom: `1px solid ${designTokens.colors.gray[1]}`, marginBottom: '12px' }}
        >
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: designTokens.colors.gray[6] }}>
            AI 분석 소요시간 (테스트)
          </h2>
          <button type="button" onClick={onClose} aria-label="닫기">
            <X size={18} />
          </button>
        </div>
        <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
          <tbody>
            {stages.map((s, i) => {
              const isTotal = i === stages.length - 1
              return (
                <tr key={s.label} style={{ borderTop: isTotal ? `1px solid ${designTokens.colors.gray[2]}` : undefined }}>
                  <td style={{ padding: '6px 0', color: designTokens.colors.gray[6], fontWeight: isTotal ? 700 : 400 }}>
                    {isTotal ? s.label : `${i + 1}. ${s.label}`}
                    {s.note && (
                      <div style={{ fontSize: '11px', color: designTokens.colors.gray[4] }}>{s.note}</div>
                    )}
                  </td>
                  <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: isTotal ? 700 : 600, whiteSpace: 'nowrap' }}>
                    {fmt(s.ms)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <div className="flex justify-end" style={{ marginTop: '16px' }}>
          <Button size="small" color="dark" onClick={onClose}>닫기</Button>
        </div>
      </div>
    </div>
  )
}

export default AiTimingModal
